/**
 * Compliance — photographs of units somebody has reported.
 *
 * WHAT THIS DESK IS FOR. The founder ruled that a listing photograph goes
 * STRAIGHT THROUGH: it appears the moment a landlord uploads it, on a unit that
 * is proven owned and on a business Orchid has verified. Nothing queues it.
 * Anybody signed in can report it, and a report HIDES IT AT ONCE, pending
 * review.
 *
 * This screen is what "pending" means. Without it, a report is a permanent
 * deletion that any signed-in person can perform on any landlord's photograph.
 *
 * THE ASYMMETRY IS DELIBERATE AND WORTH KNOWING BEFORE YOU DECIDE: the cost of
 * hiding a good photograph for a day is a landlord's mild annoyance. The cost
 * of leaving a bad one up is somebody's window on the internet. That is why the
 * photograph is already hidden by the time you see it, and why clearing it is
 * an act rather than the default.
 *
 * ---------------------------------------------------------------------------
 * IT NAMES NOBODY — NOT THE LANDLORD, AND NOT THE REPORTER.
 *
 * Prohibition 6, and it is also the right shape for the judgement. You are
 * deciding about a PHOTOGRAPH. A reviewer who can see who complained is a
 * reviewer who can be lobbied, and a reviewer who can see whose portfolio it is
 * is a reviewer deciding about a landlord.
 *
 * ---------------------------------------------------------------------------
 * AND THE REASON IS KEPT.
 *
 * Unlike the landlord business queue in this same folder, which says plainly
 * that no reason is stored: `review_reported_photo` declares
 * requires_reason_capture, the platform's log trigger refuses the action
 * without one, and it lands on the photograph as `review_reason` and in the
 * admin activity log. So there is a box here, and what goes in it is kept.
 *
 * ONCE CLEARED, A PHOTOGRAPH CANNOT BE REPORTED AGAIN. Your decision is final
 * against passers-by — otherwise one determined person hides a competitor's
 * whole portfolio on a loop, one click per round against one review per round.
 * Take that seriously in both directions.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  O, SANS, Card, Notice, PrimaryCTA, QuietButton, Spinner,
} from '../ui';
import {
  fetchReportedPhotoQueue, reviewReportedPhoto, openReportedPhoto,
} from '../lib/compliance';

/**
 * The photograph itself.
 *
 * SIGNED ON DEMAND, NOT WITH THE QUEUE. A link is minted when a reviewer asks
 * to look, so a queue of twelve reports does not scatter twelve live links to
 * the insides of twelve homes across a browser's memory for somebody who
 * opened the screen and went to lunch.
 *
 * AND IT SAYS WHEN IT CANNOT. Row security is what refuses here — script 347
 * admits a seat only to photographs awaiting judgement — and from the outside
 * that looks like a missing file. A reviewer who is told "could not load"
 * assumes a broken image; one who is told the review is closed knows what
 * happened.
 */
const Photograph = ({ storagePath }) => {
  const [state, setState] = useState({ url: null, error: null, asked: false });

  const look = async () => {
    const { data, error } = await openReportedPhoto(storagePath);
    setState({ url: data || null, error: error || null, asked: true });
  };

  if (!state.asked) {
    return (
      <div style={{ marginBottom: 10 }}>
        <QuietButton onClick={look}>Look at the photograph</QuietButton>
      </div>
    );
  }

  if (state.error) {
    return <div style={{ marginBottom: 10 }}><Notice tone="critical">{state.error}</Notice></div>;
  }

  return (
    <div style={{ marginBottom: 10 }}>
      <img
        src={state.url}
        alt="The reported photograph of this unit"
        style={{
          maxWidth: '100%', maxHeight: 320, borderRadius: 10,
          border: `1px solid ${O.hairline}`, display: 'block',
        }}
      />
      <p style={{ fontFamily: SANS, fontSize: 11, color: O.inkFaint, margin: '5px 0 0' }}>
        {/* THIS SENTENCE SAID SOMETHING UNTRUE FOR ABOUT TEN MINUTES, and the
            correction is worth keeping. It read "Your account and the moment
            are on the record", which is true of the DECISION -- that goes
            through fn_review_reported_unit_photo's reason gate -- and NOT of
            merely looking, which nothing writes down.

            A screen that tells a reviewer they are being watched when they are
            not is worse than one that says nothing: it buys the comfort of an
            audit trail without the audit trail. Whether opening a reported
            photograph should itself need a captured reason -- as view_kyc_document
            does, and this is the same class of thing -- is a decision about how
            much friction a desk carries, and it is the founder's. It is
            recorded in the ledger. */}
        This link lasts five minutes. The decision you make is recorded with
        your name; opening the photograph is not.
      </p>
    </div>
  );
};

const Row = ({ photo, onDecide, busy }) => {
  const [reason, setReason] = useState('');

  return (
    <Card style={{ marginBottom: 12 }}>
      <p style={{
        fontFamily: SANS, fontSize: 14, fontWeight: 700, color: O.ink,
        margin: '0 0 2px',
      }}>
        {/* THE UNIT, NOT THE LANDLORD. A city and a unit designator place the
            photograph without naming whose it is. */}
        {[photo.city, photo.unit_designator && `Unit ${photo.unit_designator}`]
          .filter(Boolean).join(' · ') || 'A unit'}
      </p>
      {photo.caption && (
        <p style={{ fontFamily: SANS, fontSize: 12.5, color: O.inkMuted, margin: '0 0 8px' }}>
          Caption: “{photo.caption}”
        </p>
      )}

      {/* THE PHOTOGRAPH, WHICH THIS SCREEN COULD NOT SHOW UNTIL SCRIPT 347.
          The gap was recorded in 344's own PR: the desk worked from the report
          and the caption, enough to clear an obviously vexatious report and not
          enough to uphold a borderline one. Measured then, as a real signed-in
          seat: 0 objects. The bucket is private and a compliance seat is
          neither the landlord nor somebody the listing policy admits, so both
          existing predicates said no -- correctly, and with nothing saying
          yes. 347 is the third policy, scoped to what is awaiting judgement. */}
      <Photograph storagePath={photo.storage_path} />

      <div style={{
        padding: 10, borderRadius: 10, background: 'rgba(0,0,0,0.03)', marginBottom: 10,
      }}>
        <p style={{
          fontFamily: SANS, fontSize: 11, letterSpacing: 0.4, textTransform: 'uppercase',
          color: O.inkFaint, margin: '0 0 4px',
        }}>
          What was reported
        </p>
        <p style={{ fontFamily: SANS, fontSize: 13, color: O.ink, margin: 0, lineHeight: 1.6 }}>
          {photo.report_reason}
        </p>
      </div>

      <label style={{ display: 'block', marginBottom: 10 }}>
        <span style={{
          fontFamily: SANS, fontSize: 11.5, color: O.inkMuted, display: 'block',
          marginBottom: 4,
        }}>
          Why — it is kept on the record with your name
        </span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          aria-label="Why — it is kept on the record with your name"
          style={{
            width: '100%', boxSizing: 'border-box', fontFamily: SANS, fontSize: 13,
            padding: '9px 11px', borderRadius: 9, border: `1px solid ${O.hairline}`,
            resize: 'vertical', color: O.ink, background: O.card,
          }}
        />
      </label>

      {/* BOTH BUTTONS SHARE A ROW, SO THEY SHARE A SIZE AND FILL IT. Neither is
          bigger: upholding and clearing are the same weight of decision, and a
          bigger "take it down" would be the screen leaning on a reviewer. */}
      <div style={{ display: 'flex', gap: 10 }}>
        <QuietButton
          style={{ flex: 1 }}
          disabled={busy}
          onClick={() => onDecide(photo.photo_id, false, reason)}
        >
          Clear it
        </QuietButton>
        <PrimaryCTA
          style={{ flex: 1 }}
          disabled={busy}
          onClick={() => onDecide(photo.photo_id, true, reason)}
        >
          Take it down
        </PrimaryCTA>
      </div>
    </Card>
  );
};

export default function ReportedPhotoQueue() {
  const [state, setState] = useState({ loading: true, error: null, rows: [] });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    const { data, error } = await fetchReportedPhotoQueue();
    if (error) { setState({ loading: false, error, rows: [] }); return; }
    setState({ loading: false, error: null, rows: data });
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (photoId, uphold, reason) => {
    setBusy(true);
    setNote(null);
    const { error } = await reviewReportedPhoto(photoId, uphold, reason);
    setBusy(false);
    if (error) { setNote({ tone: 'critical', text: error }); return; }
    setNote({
      tone: 'info',
      text: uphold
        ? 'Taken down. It cannot be put back from here.'
        : 'Cleared. It is showing to tenants again, and cannot be reported again.',
    });
    // RE-READ RATHER THAN PATCH IN PLACE. Whether the photograph is still in
    // the queue is the database's to state.
    await load();
  };

  if (state.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0' }}>
        <Spinner size={16} />
        <span style={{ fontFamily: SANS, fontSize: 12.5, color: O.inkMuted }}>
          Reading the queue…
        </span>
      </div>
    );
  }

  if (state.error) return <Notice tone="critical">{state.error}</Notice>;

  return (
    <div>
      <p style={{
        fontFamily: SANS, fontSize: 12.5, lineHeight: 1.7, color: O.inkMuted,
        margin: '0 0 6px', maxWidth: '74ch',
      }}>
        A photograph here is already hidden from tenants — a report hides it at
        once, before anybody has looked at it. Clearing it puts it back, and it
        cannot be reported again afterwards.
      </p>
      <p style={{
        fontFamily: SANS, fontSize: 11.5, lineHeight: 1.7, color: O.inkFaint,
        margin: '0 0 14px', maxWidth: '74ch',
      }}>
        You are deciding about a photograph. Neither the landlord nor the person
        who reported it is named here, on purpose.
      </p>

      {note && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone={note.tone}>{note.text}</Notice>
        </div>
      )}

      {state.rows.length === 0 ? (
        /* AN EMPTY QUEUE IS A SUCCESS, not a blank state to fill. MDS 2.2. */
        <Notice tone="info">
          No photograph is waiting to be looked at.
        </Notice>
      ) : (
        state.rows.map((p) => (
          <Row key={p.photo_id} photo={p} onDecide={decide} busy={busy} />
        ))
      )}
    </div>
  );
}
